import * as _ from 'lodash';
import * as moment from 'moment';
import { useEffect, useReducer } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, DonorActionCreators, RecipientActionCreators, IRecipientMinDetailsResponse, IDonorMinDetailsResponse, OrdersActionCreators, ICheekSwabOrderResponse } from '@nkr/store/index';
import { ICheekSwabRequest, IBaseResponse } from '@nkr/models/index';
import { ICheekSwabRequestValidation } from './cheek-swab-request-validation';
import { initialState, cheekSwabReducer } from './state';
import { Col, Row, Button, Grid, Panel, Form, Modal, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Utility } from '@nkr/services/utility';
import { downloadFile } from '@nkr/services/download-file';
import CheekSwabRequestFormContent from './form-content';
import usePrevious from '@nkr/services/use-previous';

export enum CheekSwabType {
    Order3gTissueTyping = 1,
    UtilizeBulkCheekSwab = 2
}

export enum CheckSwabRequestRenderType {
    Center = 0,
    Donor = 1,
    Recipient = 2
}

interface IProps {
    centerId: number,
    donorId: number,
    recipientId: number,
    isModal: boolean,
    show?: boolean,
    type?: CheekSwabType,
    onFinished?: (cancel: boolean) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void
    getDonorById: (donorId: number) => Promise<IDonorMinDetailsResponse>,
    getRecipientById: (donorId: number) => Promise<IRecipientMinDetailsResponse>,
    requestDonorCheekSwab: (donorId: number, request: ICheekSwabRequest) => Promise<ICheekSwabOrderResponse>,
    utilizeBulkDonorCheekSwab: (donorId: number, request: ICheekSwabRequest) => Promise<any>,
    requestRecipientCheekSwab: (recipientId: number, request: ICheekSwabRequest) => Promise<IBaseResponse>,
    utilizeBulkRecipientCheekSwab: (recipientId: number, request: ICheekSwabRequest) => Promise<any>,
}

type TProps = IProps & IStateProps & IDispatchProps;

const CheekSwabRequestForm = (props: TProps) => {
    const [cheekSwabState, dispatch] = useReducer(cheekSwabReducer, initialState);
    const previousShow = usePrevious(props.show);

    useEffect(() => {
        if ((props.isModal && props.show) || props.isModal == false) {
            getData();
        }
    }, [props.isModal, props.show]);

    useEffect(() => {
        if (previousShow == false && props.show == true) {
            dispatch({
                type: 'reset',
                payload: initialState
            });
        }
    }, [props.show]);

    const getData = () => {
        let entityType = props.donorId > 0 ? 'donor' : 'recipient';

        props.showLoading(`Getting ${entityType} data, one moment please...`).then(() => {
            let isDonor = props.donorId > 0;
            let isRecipient = props.recipientId > 0;

            if (isDonor) {
                props.getDonorById(props.donorId)
                    .then(response => {
                        if (response.data.success) {
                            let { firstName, lastName, address1, address2, city, state, postalCode, contactPhone, email, dob } = response.data.results;

                            dispatch({
                                type: 'afterGetData',
                                payload: {
                                    firstName,
                                    lastName,
                                    address1,
                                    address2,
                                    city,
                                    state,
                                    postalCode,
                                    contactPhone,
                                    email,
                                    dob
                                }
                            });
                        } else {
                            toast.error('Unable to complete request: ' + response);
                        }
                    })
                    .catch(response => {
                        toast.error('Unable to complete request: ' + response);

                    })
                    .finally(() => {
                        props.hideLoading();
                    });
            }

            if (isRecipient) {
                props.getRecipientById(props.recipientId)
                    .then(response => {
                        if (response.data.success) {
                            let { firstName, lastName, address1, address2, city, state, postalCode, contactPhone, email, dob } = response.data.results;

                            dispatch({
                                type: 'afterGetData',
                                payload: {
                                    firstName,
                                    lastName,
                                    address1,
                                    address2,
                                    city,
                                    state,
                                    postalCode,
                                    contactPhone,
                                    email,
                                    dob
                                }
                            });
                        } else {
                            toast.error('Unable to complete request: ' + response);
                        }
                    })
                    .catch(response => {
                        toast.error('Unable to complete request: ' + response);
                    })
                    .finally(() => {
                        props.hideLoading();
                    });
            }
        });
    };

    const isValid = (validation: ICheekSwabRequestValidation) => {
        if (validation == null) {
            return true;
        }

        let { firstName, lastName, address1, city, state, postalCode, contactPhone, email, dob } = validation;
        return firstName && lastName && address1 && city && state && postalCode && contactPhone && email && dob;
    };

    const validate = () => {
        let { firstName, lastName, address1, city, state, postalCode, contactPhone, email, dob } = cheekSwabState;

        let validation: ICheekSwabRequestValidation = {
            firstName: firstName.trim() != '',
            lastName: lastName.trim() != '',
            address1: address1.trim() != '',
            address2: true,
            city: city.trim() != '',
            state: state.trim() != '',
            postalCode: postalCode.trim() != '',
            contactPhone: contactPhone.trim() != '',
            email: Utility.isValidEmail(email),
            dob: moment(dob, 'YYYY-MM-DD').isValid()
        };

        return validation;
    };

    const requestDonorCheekSwab = () => {
        props.requestDonorCheekSwab(props.donorId, cheekSwabState)
            .then(response => {
                if (response.data.success) {
                    toast.success('Request has been completed.');

                    if (props.onFinished != null) {
                        props.onFinished(false);
                    }

                } else if (response.data.results?.existingSwab) {
                    dispatch({
                        type: 'updateMessage',
                        payload: response.data.message
                    });

                    toast.warning(response.data.message);
                } else {
                    dispatch({
                        type: 'updateMessage',
                        payload: 'Unable to complete request: ' + response.data.message
                    });

                    toast.error('Request failed.  Please contact support.');
                }
            })
            .finally(() => {
                props.hideLoading();
            });
    };

    const utilizeBulkDonorCheekSwab = () => {
        props.showLoading('Submitting request...').then(() => {
            props.utilizeBulkDonorCheekSwab(props.donorId, cheekSwabState)
                .then(response => {
                    if (response) {
                        toast.success('Request has been completed.');

                        let fileName = cheekSwabState.firstName + '_' + cheekSwabState.lastName
                            + "_CheekSwab"
                            + "_" + moment().format("YYYY_MM_DD");

                        downloadFile(response.data, fileName, response.data.type);

                        if (props.onFinished != null) {
                            props.onFinished(false);
                        }
                    } else {
                        dispatch({
                            type: 'updateMessage',
                            payload: 'Unable to complete request: ' + response.data.message
                        });
                    }
                })
                .catch(async error => {
                    if (error?.response?.data instanceof Blob) {
                        const message = await error.response.data.text();

                        dispatch({
                            type: 'updateMessage',
                            payload: message
                        });

                        toast.warning(message);
                    } else {
                        toast.error('Unable to complete request.');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const requestRecipientCheekSwab = () => {
        props.showLoading('Submitting request...').then(() => {
            props.requestRecipientCheekSwab(props.recipientId, cheekSwabState)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Request has been completed.');

                        if (props.onFinished != null) {
                            props.onFinished(false);
                        }
                    } else if (response.data.results?.existingSwab) {
                        dispatch({
                            type: 'updateMessage',
                            payload: response.data.message
                        });

                        toast.warning(response.data.message);
                    } else {
                        dispatch({
                            type: 'updateMessage',
                            payload: 'Unable to complete request: ' + response.data.message
                        });
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const utilizeBulkRecipientCheekSwab = () => {
        props.showLoading('Submitting request...').then(() => {
            props.utilizeBulkRecipientCheekSwab(props.recipientId, cheekSwabState)
                .then(response => {
                    if (response) {
                        toast.success('Request has been completed.');

                        let fileName = cheekSwabState.firstName + '_' + cheekSwabState.lastName
                            + "_CheekSwab"
                            + "_" + moment().format("YYYY_MM_DD");

                        downloadFile(response.data, fileName, response.data.type);

                        if (props.onFinished != null) {
                            props.onFinished(false);
                        }
                    } else {
                        dispatch({
                            type: 'updateMessage',
                            payload: 'Unable to complete request: ' + response.data.message
                        });
                    }
                })
                .catch(async error => {
                    if (error?.response?.data instanceof Blob) {
                        const message = await error.response.data.text();

                        dispatch({
                            type: 'updateMessage',
                            payload: message
                        });

                        toast.warning(message);
                    } else {
                        toast.error('Unable to complete request.');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleTextInputChange = (name: string, value: any) => {
        dispatch({
            type: 'inputChange',
            payload: {
                name,
                value
            }
        });
    };

    const handleRequest = () => {
        let validation = validate();

        dispatch({
            type: 'submit',
            payload: {
                validation,
                dirty: true
            }
        });

        if (isValid(validation)) {
            let isDonor = props.donorId > 0;

            if (isDonor) {
                props.showLoading('Submitting request...').then(() => {
                    if (props.type == CheekSwabType.Order3gTissueTyping) {
                        requestDonorCheekSwab();
                    } else {
                        utilizeBulkDonorCheekSwab();
                    }
                });
            } else {
                if (props.type == CheekSwabType.Order3gTissueTyping) {
                    requestRecipientCheekSwab();
                } else {
                    utilizeBulkRecipientCheekSwab();
                }
            }
        }
    };

    const renderForm = (type: CheckSwabRequestRenderType) => {
        return (
            <Form>
                <CheekSwabRequestFormContent
                    type={type}
                    firstName={cheekSwabState.firstName}
                    lastName={cheekSwabState.lastName}
                    address1={cheekSwabState.address1}
                    address2={cheekSwabState.address2}
                    city={cheekSwabState.city}
                    state={cheekSwabState.state}
                    postalCode={cheekSwabState.postalCode}
                    contactPhone={cheekSwabState.contactPhone}
                    email={cheekSwabState.email}
                    dob={cheekSwabState.dob}
                    dirty={cheekSwabState.dirty}
                    validation={cheekSwabState.validation}
                    onChange={handleTextInputChange}
                />
            </Form>
        );
    };

    const renderAsModal = (type: CheckSwabRequestRenderType) => {
        let title = props.type == CheekSwabType.Order3gTissueTyping ? 'Request Cheek Swab Kit' : 'Utilize an On-Hand Bulk Cheek Swab Kit';
        let buttonLabel = props.type == CheekSwabType.Order3gTissueTyping ? 'Request Cheek Swab Kit' : 'Generate Requisition';

        title += isDonor ? ' for Donor' : ' for Recipient';

        return (
            <Modal backdrop='static' show={props.show} onHide={() => props.onFinished(true)}>
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isValid(cheekSwabState.validation) == false && (
                        <Alert bsStyle='danger' bsSize='sm'>The items in red are required...</Alert>
                    )}

                    {cheekSwabState.message != '' && (
                        <Alert bsStyle='warning' bsSize='sm'>{cheekSwabState.message}</Alert>
                    )}

                    {renderForm(type)}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' onClick={handleRequest}>{buttonLabel}</Button>
                    <Button onClick={() => props.onFinished(true)}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderAsPage = (type: CheckSwabRequestRenderType) => {
        return (
            <Grid>
                <Row>
                    <h2 style={{ textAlign: 'center' }}>Request Cheek Swab Kit</h2>
                </Row>

                <Row>
                    <Col smOffset={3} sm={6}>
                        <Panel>
                            <Panel.Heading><h4>Confirm {entityType} Demographic Information Here</h4></Panel.Heading>
                            <Panel.Body>
                                {renderForm(type)}
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>

                <Row>
                    <Col smOffset={3} sm={6}>
                        <div className='text-center' style={{ width: '100%' }}>
                            <Button bsStyle='primary' onClick={handleRequest} >Request Cheek Swab Kit</Button>
                        </div>
                    </Col>
                </Row>
            </Grid>
        );
    };

    let entityType = props.donorId > 0 ? 'Donor' : 'Recipient';

    let type = CheckSwabRequestRenderType.Center;
    let title = 'Confirm ' + entityType + ' Demographic Information Here';

    let isDonor = props.donorId > 0;
    let isRecipient = props.recipientId > 0;

    if (isDonor) {
        type = CheckSwabRequestRenderType.Donor;
        title = 'Confirm Donor Demographic Information Here';
    }

    if (isRecipient) {
        type = CheckSwabRequestRenderType.Recipient;
        title = 'Confirm Recipient Demographic Information Here';
    }

    if (props.isModal) {
        return renderAsModal(type);
    }

    return renderAsPage(type);
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        requestDonorCheekSwab: (donorId, request) => {
            return dispatch(OrdersActionCreators.requestDonorCheekSwab(donorId, request))
        },
        utilizeBulkDonorCheekSwab: (recipientId, request) => {
            return dispatch(OrdersActionCreators.createDonorOrder(recipientId, request));
        },
        requestRecipientCheekSwab: (recipientId, request) => {
            return dispatch(OrdersActionCreators.requestRecipientCheekSwab(recipientId, request))
        },
        utilizeBulkRecipientCheekSwab: (recipientId, request) => {
            return dispatch(OrdersActionCreators.createRecipientOrder(recipientId, request));
        },
        getDonorById: (donorId) => {
            return dispatch(DonorActionCreators.getDonorMinById(donorId))
        },
        getRecipientById: (recipientId) => {
            return dispatch(RecipientActionCreators.getRecipientMinById(recipientId))
        },
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        }
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CheekSwabRequestForm);